import React, { useEffect, useRef } from "react";
import "./sidebar-root-menu.css";
import { SideBarRootItem } from "./sidebar-root-item";
import { MenuId, MenuIds, useMenus } from "../../../hooks/useMenus";
import { useDefinitionFeatures, useSpreadFeatures } from "../../../hooks/useDefinitionFeatures";
import { useIsProductAdvancedEditMode } from "../../../hooks/useIsProductAdvancedEditMode";
import { useGoogleAnalytics } from "../../../hooks/useGoogleAnalytics";
import { useLabels } from "../../../hooks/useLabels";

import { labels } from "./labels";
import { useAlternativeProducts } from "../../../hooks/useAlternativeProducts";
import { useProductOptions } from "../../../hooks/useProductOptions";
import { useSupportedProductStartDate } from "../../../hooks/useSupportedProductStartDate";
import { useDashboardContext } from "../../../contexts/dashboard-context";
import { useSelectedSpread } from "../../../hooks/useSelection";
import { useFeatureFlagToggle } from "../../../hooks/useFeatureFlags";
import { useErrorHandling } from "../../../hooks/useErrorHandling";
import { useMapZones } from "../../bottom-menus/bottom-root-menu/use-map-zones";
import { showEventDialogAsync } from "../../dialogs/events-dialog";
import { useAppContext } from "../../../hooks/useAppContext";
import { useAddTextZone } from "../../../hooks/useAddTextZone";
import { useSymbolsCount } from "../../../hooks/useSymbols";

/** Get the root menuId for the current menuId */
function getActiveRootMenuId(menuId: MenuId | undefined) {
	// Used switch, so we have handled all cases
	switch (menuId) {
		// Photo
		case MenuIds.Photo:
		case MenuIds.PhotoRotate:
		case MenuIds.PhotoTransform:
		case MenuIds.PhotoZoneDepth:
		case MenuIds.PhotoSelection:
		case MenuIds.PhotoEditing:
		case MenuIds.PhotoBrightness:
		case MenuIds.PhotoContrast:
		case MenuIds.PhotoSaturation:
		case MenuIds.PhotoFilters:
			return MenuIds.Photo;
		// Text
		case MenuIds.Text:
		case MenuIds.TextStyle:
		case MenuIds.TextHAlignment:
		case MenuIds.TextVAlignment:
		case MenuIds.TextSize:
		case MenuIds.TextZoneAdd:
		case MenuIds.TextZoneDepth:
		case MenuIds.TextValue:
		case MenuIds.TextColor:
		case MenuIds.FontFamilies:
			return MenuIds.Text;

		case MenuIds.Layouts:
			return MenuIds.Layouts;

		case MenuIds.Qr:
		case MenuIds.QrAdd:
		case MenuIds.QrEdit:
			return MenuIds.Qr;

		case MenuIds.Symbols:
		case MenuIds.SymbolsList:
		case MenuIds.SymbolZoneDepth:
			return MenuIds.Symbols;

		case MenuIds.Designs:
			return MenuIds.Designs;

		case MenuIds.Options:
		case MenuIds.AlternativeProducts:
		case MenuIds.ProductOptions:
		case MenuIds.DateSelection:
			return MenuIds.Options;

		case MenuIds.Backgrounds:
		case MenuIds.BackgroundColor:
		case MenuIds.BackgroundPattern:
			return MenuIds.Backgrounds;

		// No sidebar menu
		case undefined:
		case MenuIds.Root:
		case MenuIds.Map:
		case MenuIds.Events:
			return undefined;
		default:
			eXpress.utils.exhaustiveFail(menuId);
			return undefined;
	}
}

function SidebarRootMenu() {
	const {
		menuId,
		actions: { setMenu },
	} = useMenus();

	const [
		[
			optionsTitle,
			photoMenuTitle,
			mapMenuTitle,
			textMenuTitle,
			designsMenuTitle,
			eventsTitle,
			_filtersTitle,
			symbolsMenuTitle,
		],
	] = useLabels([
		labels.options,
		labels.photo,
		labels.map,
		labels.text,
		labels.designs,
		labels.events,
		labels.filters,
		labels.symbols,
	]);

	const googleAnalytics = useGoogleAnalytics();
	const { createErrorHandler } = useErrorHandling();
	const dashboard = useDashboardContext();
	const isAdvancedEditModeEnabled = useIsProductAdvancedEditMode();

	const allowDesktopPhotoMenus = useFeatureFlagToggle("allowDesktopPhotoMenus");
	const allowDesktopSymbolMenus = useFeatureFlagToggle("allowDesktopSymbolMenus");
	const { openZoneMapAsync, isMapsSupportedOnDefinition: showMapsButton } = useMapZones();

	const {
		hasFillablePhotoZonesInDefinition,
		hasFillableTextZonesInDefinition,
		areDesignsSupported,
		areSymbolsSupported,
	} = useDefinitionFeatures();

	const currentSpread = useSelectedSpread();

	// Symbol zone
	const { hasFillableSymbolZonesInSpread } = useSpreadFeatures();
	const areSymbolsDisabled = !currentSpread?.pageZones.some(
		(page) => hasFillableSymbolZonesInSpread || (page && dashboard.canAddSymbolZone(page)),
	);
	const symbolsCount = useSymbolsCount();
	const showSymbolsButton = allowDesktopSymbolMenus && areSymbolsSupported && !!symbolsCount;

	// Text zone
	const { canAddTextZoneToSpread } = useAddTextZone();
	const showTextButton = hasFillableTextZonesInDefinition || isAdvancedEditModeEnabled;
	const showPhotoButton =
		allowDesktopPhotoMenus && (hasFillablePhotoZonesInDefinition || isAdvancedEditModeEnabled);
	const canChangeMap = !!openZoneMapAsync;

	// Options
	const { alternativeProductsGroups, isLoading: areAlternativeProductsLoading } =
		useAlternativeProducts();
	const { productOptionGroup, isLoading: areProductOptionsLoading } = useProductOptions();
	const isProductStartDateSupported = useSupportedProductStartDate();
	const hasProductAlternatives = !!alternativeProductsGroups?.length;
	const hasProductOptions = !!productOptionGroup;

	const showCalendarEvents = dashboard.canChangeCalendarEvents();
	const showOptionsMenu =
		hasProductAlternatives || hasProductOptions || isProductStartDateSupported;

	// Log When Photo menu was needed, which is not yet supported
	const { client } = useAppContext();
	const didLogUnsupportedPhotoMenu = useRef(false);
	useEffect(() => {
		if (
			!eXpress.isDebugMode &&
			!didLogUnsupportedPhotoMenu.current &&
			!allowDesktopPhotoMenus &&
			(hasFillablePhotoZonesInDefinition || isAdvancedEditModeEnabled)
		) {
			const message = `Unsupported Photo button is required in sidebar menu.`;
			const exception = new eXpress.GeneralException(
				"UnsupportedPhotoSidebarMenuException",
				message,
			);
			client.logErrorAsync(exception, false);
			didLogUnsupportedPhotoMenu.current = true;
		}
	}, [
		client,
		allowDesktopPhotoMenus,
		hasFillablePhotoZonesInDefinition,
		isAdvancedEditModeEnabled,
	]);

	if (areAlternativeProductsLoading || areProductOptionsLoading) return null;

	let activeMenuId = getActiveRootMenuId(menuId);

	// Don't show a disabled menu as active
	if (activeMenuId === MenuIds.Symbols && areSymbolsDisabled) {
		activeMenuId = undefined;
	}

	return (
		<div className="sidebar-root-menu">
			{showPhotoButton && (
				<SideBarRootItem
					isActive={activeMenuId === MenuIds.Photo}
					iconName="photo"
					title={photoMenuTitle ?? ""}
					onClick={() => {
						googleAnalytics.trackEventToGa({
							trackingAction: "RootPhotosClicked",
						});

						if (activeMenuId === MenuIds.Photo) {
							setMenu(MenuIds.Root);
						} else {
							setMenu(MenuIds.Photo);
						}
					}}
				/>
			)}

			{showMapsButton && (
				<SideBarRootItem
					isActive={false}
					isDisabled={!canChangeMap}
					iconName="map"
					title={mapMenuTitle ?? ""}
					onClick={() => {
						googleAnalytics.trackEventToGa({
							trackingAction: "RootMapsClicked",
						});

						setMenu(MenuIds.Map);
						openZoneMapAsync?.().catch(createErrorHandler());
					}}
				/>
			)}

			{showCalendarEvents && (
				<SideBarRootItem
					isActive={false}
					iconName="calendar"
					title={eventsTitle ?? ""}
					onClick={() => {
						googleAnalytics.trackEventToGa({
							trackingAction: "EventsClicked",
						});

						setMenu(MenuIds.Events);

						showEventDialogAsync()
							.then((result) => {
								if (!result) return;
								if (!dashboard.canChangeCalendarEvents(result)) return;

								dashboard.changeCalendarEvents(result);
							})
							.catch(createErrorHandler());
					}}
				/>
			)}

			{showTextButton && (
				<SideBarRootItem
					isActive={activeMenuId === MenuIds.Text}
					iconName="text"
					isDisabled={
						currentSpread
							? !(
									currentSpread.getFillableTextZones(false).length > 0 ||
									canAddTextZoneToSpread
							  )
							: true
					}
					title={textMenuTitle ?? ""}
					onClick={() => {
						googleAnalytics.trackEventToGa({
							trackingAction: "RootTextClicked",
						});

						if (menuId === MenuIds.TextColor || menuId === MenuIds.FontFamilies) {
							setMenu(MenuIds.Text);
						} else if (activeMenuId === MenuIds.Text) {
							setMenu(MenuIds.Root);
						} else {
							setMenu(MenuIds.Text);
						}
					}}
				/>
			)}

			{showSymbolsButton && (
				<SideBarRootItem
					isActive={activeMenuId === MenuIds.Symbols}
					isDisabled={areSymbolsDisabled}
					iconName="symbols"
					title={symbolsMenuTitle ?? ""}
					onClick={() => {
						setMenu(activeMenuId === MenuIds.Symbols ? MenuIds.Root : MenuIds.Symbols);
					}}
				/>
			)}

			{areDesignsSupported && (
				<SideBarRootItem
					isActive={activeMenuId === MenuIds.Designs}
					iconName="designs"
					title={designsMenuTitle ?? ""}
					onClick={() => {
						googleAnalytics.trackEventToGa({
							trackingAction: "RootDesignsClicked",
						});
						setMenu(menuId === MenuIds.Designs ? MenuIds.Root : MenuIds.Designs);
					}}
				/>
			)}

			{showOptionsMenu && (
				<SideBarRootItem
					isActive={activeMenuId === MenuIds.Options}
					iconName="options"
					title={optionsTitle ?? ""}
					onClick={() => {
						// Options are not logged yet
						// googleAnalytics.trackEventToGa({
						// 	trackingAction: "",
						// });

						// when resizing to the mobile view, it will show collapsed option menus, while in mobile view it auto expands if there is only one
						// If that is a requirement, we need to use the same code here as use in mobile view when clicking options
						if (activeMenuId === MenuIds.Options) {
							setMenu(MenuIds.Root);
						} else {
							setMenu(MenuIds.Options);
						}
					}}
				/>
			)}
		</div>
	);
}

export { SidebarRootMenu };
